/**
 * IOSA Audit Module Page
 * Comprehensive IATA Operational Safety Audit implementation
 * Covers all 8 IOSA sections with Risk-Based methodology
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import {
  Building2, Plane, Radio, Wrench, Users, Truck, Package, Shield,
  CheckCircle2, Clock, AlertTriangle, Target, Plus, Search, Filter,
  FileText, ClipboardCheck, TrendingUp, BarChart3, Play, Eye, Download,
  ChevronRight, Loader2, Award, Activity, AlertCircle, Settings, Sparkles,
  XCircle, Edit, Trash2, Calendar, User, FileWarning, ChevronDown,
  Upload, Image, Mic, File, Paperclip, X, Plug, RefreshCw, Globe, Database,
  ArrowUpDown, Bell, Share2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// IOSA Section Configuration - using translation keys
const IOSA_SECTIONS_CONFIG = {
  ORG: { icon: Building2, color: 'blue', items: 125 },
  FLT: { icon: Plane, color: 'indigo', items: 150 },
  DSP: { icon: Radio, color: 'cyan', items: 85 },
  MNT: { icon: Wrench, color: 'orange', items: 140 },
  CAB: { icon: Users, color: 'pink', items: 105 },
  GRH: { icon: Truck, color: 'emerald', items: 120 },
  CGO: { icon: Package, color: 'amber', items: 95 },
  SEC: { icon: Shield, color: 'red', items: 105 }
};

// Helper to get translated section name
const getSectionName = (code, t) => t(`iosa.sections.${code}`);

// Helper to get conformity status label
const getConformityLabel = (value, t) => t(`iosa.conformityStatus.${value}`);

// Helper to get finding type label
const getFindingTypeLabel = (value, t) => t(`iosa.findingTypes.${value}`);

// Helper to get severity label
const getSeverityLabel = (value, t) => t(`iosa.severityLevels.${value}`);

// Conformity status values with colors
const CONFORMITY_STATUS_VALUES = [
  { value: 'not_assessed', color: 'slate' },
  { value: 'documented', color: 'blue' },
  { value: 'implemented', color: 'cyan' },
  { value: 'effective', color: 'green' },
  { value: 'not_applicable', color: 'gray' },
  { value: 'non_conformity', color: 'red' }
];

// Finding type values with colors
const FINDING_TYPE_VALUES = [
  { value: 'finding', color: 'red' },
  { value: 'observation', color: 'amber' },
  { value: 'opportunity', color: 'blue' }
];

// Severity level values with colors
const SEVERITY_LEVEL_VALUES = [
  { value: 'level_1', color: 'yellow' },
  { value: 'level_2', color: 'orange' },
  { value: 'level_3', color: 'red' }
];

export default function IOSAAuditPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [sections, setSections] = useState([]);
  const [audits, setAudits] = useState([]);
  const [findings, setFindings] = useState({ findings: [], stats: {} });
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [auditDetail, setAuditDetail] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [sectionDetail, setSectionDetail] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [smsMaturity, setSmsMaturity] = useState(null);
  
  // Build IOSA_SECTIONS with translated names
  const IOSA_SECTIONS = Object.fromEntries(
    Object.entries(IOSA_SECTIONS_CONFIG).map(([code, config]) => [
      code,
      { ...config, name: t(`iosa.sections.${code}`) }
    ])
  );
  
  // Build translated status/type arrays
  const CONFORMITY_STATUSES = CONFORMITY_STATUS_VALUES.map(s => ({
    ...s,
    label: t(`iosa.conformityStatus.${s.value}`)
  }));
  
  const FINDING_TYPES = FINDING_TYPE_VALUES.map(f => ({
    ...f,
    label: t(`iosa.findingTypes.${f.value}`)
  }));
  
  const SEVERITY_LEVELS = SEVERITY_LEVEL_VALUES.map(s => ({
    ...s,
    label: t(`iosa.severityLevels.${s.value}`)
  }));
  
  // Evidence states
  const [showEvidenceDialog, setShowEvidenceDialog] = useState(false);
  const [selectedFindingForEvidence, setSelectedFindingForEvidence] = useState(null);
  const [findingEvidence, setFindingEvidence] = useState({ evidence: [], by_type: {} });
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [evidenceForm, setEvidenceForm] = useState({
    evidence_type: 'document',
    description: '',
    interview_subject: ''
  });
  
  // IATA Updates & IDX Integration states
  const [iataUpdateStatus, setIataUpdateStatus] = useState(null);
  const [idxStatus, setIdxStatus] = useState(null);
  const [idxAlerts, setIdxAlerts] = useState([]);
  const [syncingIdx, setSyncingIdx] = useState(false);
  const [checkingUpdates, setCheckingUpdates] = useState(false);
  
  // Dialog states
  const [showNewAuditDialog, setShowNewAuditDialog] = useState(false);
  const [showAuditDetailDialog, setShowAuditDetailDialog] = useState(false);
  const [showFindingDialog, setShowFindingDialog] = useState(false);
  const [showAssessmentDialog, setShowAssessmentDialog] = useState(false);
  const [showSMSMaturityDialog, setShowSMSMaturityDialog] = useState(false);
  const [selectedISARP, setSelectedISARP] = useState(null);
  const [editingFinding, setEditingFinding] = useState(null);

  // Form states
  const [auditForm, setAuditForm] = useState({
    audit_type: 'renewal',
    methodology: 'rbi',
    organization_name: '',
    aoc_number: '',
    iata_code: '',
    planned_start_date: '',
    planned_end_date: '',
    lead_auditor: '',
    sections_in_scope: Object.keys(IOSA_SECTIONS_CONFIG)
  });

  const [assessmentForm, setAssessmentForm] = useState({
    conformity_status: 'not_assessed',
    evidence_summary: '',
    notes: ''
  });

  const [findingForm, setFindingForm] = useState({
    finding_type: 'finding',
    severity: 'level_1',
    title: '',
    description: '',
    root_cause: '',
    corrective_action_required: '',
    preventive_action: '',
    due_date: ''
  });

  const [smsMaturityForm, setSmsMaturityForm] = useState({
    policy_objectives: 3,
    risk_management: 3,
    safety_assurance: 3,
    safety_promotion: 3,
    strengths: '',
    improvement_areas: '',
    recommendations: '',
    notes: ''
  });

  const getApiHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  // Fetch dashboard data
  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/iosa/dashboard`, { headers: getApiHeaders() });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    }
  };

  // Fetch sections
  const fetchSections = async () => {
    try {
      const res = await fetch(`${API_URL}/api/iosa/sections`, { headers: getApiHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSections(data.sections);
      }
    } catch (err) {
      console.error('Error fetching sections:', err);
    }
  };

  // Fetch audits
  const fetchAudits = async () => {
    try {
      const res = await fetch(`${API_URL}/api/iosa/audits`, { headers: getApiHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAudits(data.audits || []);
      }
    } catch (err) {
      console.error('Error fetching audits:', err);
    }
  };

  // Fetch findings
  const fetchFindings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/iosa/findings`, { headers: getApiHeaders() });
      if (res.ok) {
        const data = await res.json();
        setFindings(data);
      }
    } catch (err) {
      console.error('Error fetching findings:', err);
    }
  };

  // Fetch audit detail
  const fetchAuditDetail = async (auditId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/iosa/audits/${auditId}`, { headers: getApiHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAuditDetail(data);
        setSelectedAudit(data.audit);
        
        // Also fetch SMS maturity
        const smsRes = await fetch(`${API_URL}/api/iosa/audits/${auditId}/sms-maturity`, { headers: getApiHeaders() });
        if (smsRes.ok) {
          const smsData = await smsRes.json();
          setSmsMaturity(smsData);
        }
      }
    } catch (err) {
      console.error('Error fetching audit detail:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch section detail
  const fetchSectionDetail = async (sectionCode) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/iosa/sections/${sectionCode}`, { headers: getApiHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSectionDetail(data);
      }
    } catch (err) {
      console.error('Error fetching section:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch checklist
  const fetchChecklist = async (sectionCode) => {
    try {
      const res = await fetch(`${API_URL}/api/iosa/checklists/${sectionCode}`, { headers: getApiHeaders() });
      if (res.ok) {
        const data = await res.json();
        setChecklist(data);
      }
    } catch (err) {
      console.error('Error fetching checklist:', err);
    }
  };

  // Create audit
  const createAudit = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        audit_type: auditForm.audit_type,
        methodology: auditForm.methodology,
        organization_name: auditForm.organization_name,
        planned_start_date: auditForm.planned_start_date,
        planned_end_date: auditForm.planned_end_date,
        lead_auditor: auditForm.lead_auditor
      });
      if (auditForm.aoc_number) params.append('aoc_number', auditForm.aoc_number);
      if (auditForm.iata_code) params.append('iata_code', auditForm.iata_code);
      auditForm.sections_in_scope.forEach(s => params.append('sections_in_scope', s));

      const res = await fetch(`${API_URL}/api/iosa/audits?${params}`, {
        method: 'POST',
        headers: getApiHeaders()
      });

      if (res.ok) {
        toast.success('IOSA Audit created successfully');
        setShowNewAuditDialog(false);
        fetchAudits();
        fetchDashboard();
        resetAuditForm();
      } else {
        toast.error('Failed to create audit');
      }
    } catch (err) {
      toast.error('Error creating audit');
    } finally {
      setLoading(false);
    }
  };

  // Create finding
  const createFinding = async () => {
    if (!selectedAudit) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        audit_id: selectedAudit.id,
        isarp_code: selectedISARP?.code || findingForm.isarp_code || 'ORG 1.1.1',
        finding_type: findingForm.finding_type,
        title: findingForm.title,
        description: findingForm.description,
        corrective_action_required: findingForm.corrective_action_required
      });
      if (findingForm.severity) params.append('severity', findingForm.severity);
      if (findingForm.root_cause) params.append('root_cause', findingForm.root_cause);
      if (findingForm.preventive_action) params.append('preventive_action', findingForm.preventive_action);
      if (findingForm.due_date) params.append('due_date', findingForm.due_date);

      const res = await fetch(`${API_URL}/api/iosa/findings?${params}`, {
        method: 'POST',
        headers: getApiHeaders()
      });

      if (res.ok) {
        toast.success('Finding created successfully');
        setShowFindingDialog(false);
        fetchFindings();
        fetchDashboard();
        if (selectedAudit?.id) fetchAuditDetail(selectedAudit.id);
        resetFindingForm();
      } else {
        toast.error('Failed to create finding');
      }
    } catch (err) {
      toast.error('Error creating finding');
    } finally {
      setLoading(false);
    }
  };

  // Update finding status
  const updateFindingStatus = async (findingId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/iosa/findings/${findingId}?status=${status}`, {
        method: 'PUT',
        headers: getApiHeaders()
      });
      if (res.ok) {
        toast.success('Finding status updated');
        fetchFindings();
        fetchDashboard();
      }
    } catch (err) {
      toast.error('Error updating finding');
    }
  };

  // Fetch evidence for a finding
  const fetchFindingEvidence = async (findingId) => {
    try {
      const res = await fetch(`${API_URL}/api/iosa/findings/${findingId}/evidence`, { headers: getApiHeaders() });
      if (res.ok) {
        const data = await res.json();
        setFindingEvidence(data);
      }
    } catch (err) {
      console.error('Error fetching evidence:', err);
    }
  };

  // Upload evidence
  const uploadEvidence = async (file) => {
    if (!selectedFindingForEvidence) return;
    setUploadingEvidence(true);
    
    try {
      // Read file as base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1]; // Remove data:...;base64, prefix
        
        const params = new URLSearchParams({
          evidence_type: evidenceForm.evidence_type,
          file_name: file.name,
          file_data: base64
        });
        if (evidenceForm.description) params.append('description', evidenceForm.description);
        if (evidenceForm.interview_subject) params.append('interview_subject', evidenceForm.interview_subject);

        const res = await fetch(`${API_URL}/api/iosa/findings/${selectedFindingForEvidence.id}/evidence?${params}`, {
          method: 'POST',
          headers: getApiHeaders()
        });

        if (res.ok) {
          toast.success('Evidence uploaded successfully');
          fetchFindingEvidence(selectedFindingForEvidence.id);
          fetchFindings();
          setEvidenceForm({ evidence_type: 'document', description: '', interview_subject: '' });
        } else {
          toast.error('Failed to upload evidence');
        }
        setUploadingEvidence(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error('Error uploading evidence');
      setUploadingEvidence(false);
    }
  };

  // Delete evidence
  const deleteEvidence = async (evidenceId) => {
    try {
      const res = await fetch(`${API_URL}/api/iosa/evidence/${evidenceId}`, {
        method: 'DELETE',
        headers: getApiHeaders()
      });
      if (res.ok) {
        toast.success('Evidence deleted');
        if (selectedFindingForEvidence) {
          fetchFindingEvidence(selectedFindingForEvidence.id);
        }
        fetchFindings();
      }
    } catch (err) {
      toast.error('Error deleting evidence');
    }
  };

  // Open evidence dialog
  const openEvidenceDialog = (finding) => {
    setSelectedFindingForEvidence(finding);
    fetchFindingEvidence(finding.id);
    setShowEvidenceDialog(true);
  };

  // Save SMS Maturity
  const saveSMSMaturity = async () => {
    if (!selectedAudit) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        policy_objectives: smsMaturityForm.policy_objectives.toString(),
        risk_management: smsMaturityForm.risk_management.toString(),
        safety_assurance: smsMaturityForm.safety_assurance.toString(),
        safety_promotion: smsMaturityForm.safety_promotion.toString()
      });
      
      // Add arrays as JSON
      if (smsMaturityForm.strengths) {
        smsMaturityForm.strengths.split('\n').filter(s => s.trim()).forEach(s => params.append('strengths', s.trim()));
      }
      if (smsMaturityForm.improvement_areas) {
        smsMaturityForm.improvement_areas.split('\n').filter(s => s.trim()).forEach(s => params.append('improvement_areas', s.trim()));
      }
      if (smsMaturityForm.recommendations) {
        smsMaturityForm.recommendations.split('\n').filter(s => s.trim()).forEach(s => params.append('recommendations', s.trim()));
      }
      if (smsMaturityForm.notes) params.append('notes', smsMaturityForm.notes);

      const res = await fetch(`${API_URL}/api/iosa/audits/${selectedAudit.id}/sms-maturity?${params}`, {
        method: 'POST',
        headers: getApiHeaders()
      });

      if (res.ok) {
        toast.success('SMS Maturity assessment saved');
        setShowSMSMaturityDialog(false);
        fetchAuditDetail(selectedAudit.id);
      } else {
        toast.error('Failed to save SMS Maturity');
      }
    } catch (err) {
      toast.error('Error saving SMS Maturity');
    } finally {
      setLoading(false);
    }
  };

  // Seed ISARPs
  const seedISARPs = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/iosa/isarps/seed`, {
        method: 'POST',
        headers: getApiHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Seeded ${data.count} ISARPs`);
        fetchSections();
      } else {
        toast.error('Failed to seed ISARPs');
      }
    } catch (err) {
      toast.error('Error seeding ISARPs');
    } finally {
      setLoading(false);
    }
  };

  // Seed comprehensive demo data
  const seedDemoData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/iosa/seed-comprehensive`, {
        method: 'POST',
        headers: getApiHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        toast.success('Demo data seeded successfully');
        fetchDashboard();
        fetchAudits();
        fetchFindings();
      } else {
        toast.error('Failed to seed demo data');
      }
    } catch (err) {
      toast.error('Error seeding demo data');
    } finally {
      setLoading(false);
    }
  };

  const resetAuditForm = () => {
    setAuditForm({
      audit_type: 'renewal',
      methodology: 'rbi',
      organization_name: '',
      aoc_number: '',
      iata_code: '',
      planned_start_date: '',
      planned_end_date: '',
      lead_auditor: '',
      sections_in_scope: Object.keys(IOSA_SECTIONS)
    });
  };

  const resetFindingForm = () => {
    setFindingForm({
      finding_type: 'finding',
      severity: 'level_1',
      title: '',
      description: '',
      root_cause: '',
      corrective_action_required: '',
      preventive_action: '',
      due_date: ''
    });
    setSelectedISARP(null);
  };

  // ==================== IATA UPDATES FUNCTIONS ====================
  
  const fetchIataUpdateStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/iosa/iata-updates/status`, { headers: getApiHeaders() });
      if (res.ok) {
        const data = await res.json();
        setIataUpdateStatus(data);
      }
    } catch (err) {
      console.error('Error fetching IATA update status:', err);
    }
  };

  const checkForUpdates = async () => {
    setCheckingUpdates(true);
    try {
      const res = await fetch(`${API_URL}/api/iosa/iata-updates/check`, {
        method: 'POST',
        headers: getApiHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.update_available ? 'Update available!' : 'Already up to date');
        fetchIataUpdateStatus();
      }
    } catch (err) {
      toast.error('Error checking for updates');
    } finally {
      setCheckingUpdates(false);
    }
  };

  const applyIataUpdate = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/iosa/iata-updates/apply`, {
        method: 'POST',
        headers: getApiHeaders()
      });
      if (res.ok) {
        toast.success('IATA update applied successfully');
        fetchIataUpdateStatus();
      }
    } catch (err) {
      toast.error('Error applying update');
    } finally {
      setLoading(false);
    }
  };

  // ==================== IDX INTEGRATION FUNCTIONS ====================
  
  const fetchIdxStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/iosa/idx/status`, { headers: getApiHeaders() });
      if (res.ok) {
        const data = await res.json();
        setIdxStatus(data);
      }
    } catch (err) {
      console.error('Error fetching IDX status:', err);
    }
  };

  const fetchIdxAlerts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/iosa/idx/alerts`, { headers: getApiHeaders() });
      if (res.ok) {
        const data = await res.json();
        setIdxAlerts(data.alerts || []);
      }
    } catch (err) {
      console.error('Error fetching IDX alerts:', err);
    }
  };

  const syncIdxData = async (direction = 'both') => {
    setSyncingIdx(true);
    try {
      const res = await fetch(`${API_URL}/api/iosa/idx/sync?direction=${direction}`, {
        method: 'POST',
        headers: getApiHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`IDX sync completed: ${data.sync_result.results.exported.incidents} exported, ${data.sync_result.results.imported.industry_alerts} alerts imported`);
        fetchIdxStatus();
        fetchIdxAlerts();
      }
    } catch (err) {
      toast.error('Error syncing with IDX');
    } finally {
      setSyncingIdx(false);
    }
  };

  const importIdxAlerts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/iosa/idx/import-alerts`, {
        method: 'POST',
        headers: getApiHeaders()
      });
      if (res.ok) {
        toast.success('IDX alerts imported');
        fetchIdxAlerts();
      }
    } catch (err) {
      toast.error('Error importing alerts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchSections();
    fetchAudits();
    fetchFindings();
    fetchIataUpdateStatus();
    fetchIdxStatus();
    fetchIdxAlerts();
  }, []);

  useEffect(() => {
    if (selectedSection) {
      fetchSectionDetail(selectedSection);
      fetchChecklist(selectedSection);
    }
  }, [selectedSection]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': case 'closed': return 'bg-green-500/20 text-green-400';
      case 'in_progress': case 'documentation_review': case 'onsite': return 'bg-amber-500/20 text-amber-400';
      case 'planned': return 'bg-blue-500/20 text-blue-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'level_3': return 'bg-red-500/20 text-red-400 border-red-500/50';
      case 'level_2': return 'bg-orange-500/20 text-orange-400 border-orange-500/50';
      case 'level_1': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="iosa-audit-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Award className="w-8 h-8 text-amber-400" />
            {t('iosa.title')}
          </h1>
          <p className="text-slate-400 mt-1">{t('iosa.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={seedDemoData} disabled={loading} data-testid="seed-demo-btn">
            <Sparkles className="w-4 h-4 mr-2" />
            {t('iosa.seedData')}
          </Button>
          <Button onClick={() => setShowNewAuditDialog(true)} data-testid="new-audit-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('iosa.newAudit')}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <BarChart3 className="w-4 h-4 mr-2" />
            {t('iosa.dashboard')}
          </TabsTrigger>
          <TabsTrigger value="audits" data-testid="tab-audits">
            <ClipboardCheck className="w-4 h-4 mr-2" />
            {t('iosa.audits')}
          </TabsTrigger>
          <TabsTrigger value="standards" data-testid="tab-standards">
            <FileText className="w-4 h-4 mr-2" />
            {t('iosa.standards')}
          </TabsTrigger>
          <TabsTrigger value="findings" data-testid="tab-findings">
            <AlertTriangle className="w-4 h-4 mr-2" />
            {t('iosa.findings')}
          </TabsTrigger>
          <TabsTrigger value="sms-maturity" data-testid="tab-sms">
            <TrendingUp className="w-4 h-4 mr-2" />
            {t('iosa.smsMaturity')}
          </TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations">
            <Plug className="w-4 h-4 mr-2" />
            {t('iosa.idx.title')}
          </TabsTrigger>
        </TabsList>

        {/* DASHBOARD TAB */}
        <TabsContent value="dashboard" className="space-y-6 mt-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400 uppercase">{t('iosa.totalAudits')}</p>
                    <p className="text-2xl font-bold text-white">{dashboard?.stats?.total_audits || 0}</p>
                  </div>
                  <ClipboardCheck className="w-8 h-8 text-blue-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400 uppercase">{t('iosa.activeAudits')}</p>
                    <p className="text-2xl font-bold text-amber-400">{dashboard?.stats?.active_audits || 0}</p>
                  </div>
                  <Play className="w-8 h-8 text-amber-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400 uppercase">{t('iosa.completedAudits')}</p>
                    <p className="text-2xl font-bold text-green-400">{dashboard?.stats?.completed_audits || 0}</p>
                  </div>
                  <CheckCircle2 className="w-8 h-8 text-green-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400 uppercase">{t('iosa.openFindings')}</p>
                    <p className="text-2xl font-bold text-red-400">{dashboard?.stats?.open_findings || findings?.stats?.open || 0}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-red-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400 uppercase">{t('iosa.totalIsarps')}</p>
                    <p className="text-2xl font-bold text-purple-400">{dashboard?.stats?.total_isarps || 925}</p>
                  </div>
                  <Target className="w-8 h-8 text-purple-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* 8 IOSA Sections Grid */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-400" />
                {t('iosa.standards')} (ISM Ed. 17)
              </CardTitle>
              <CardDescription>{t('common.all')} 8 {t('iosa.standards').toLowerCase()} - ~925 ISARPs</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(IOSA_SECTIONS).map(([code, config]) => {
                  const Icon = config.icon;
                  const findingsCount = dashboard?.findings_by_section?.[code] || 0;
                  return (
                    <Card 
                      key={code} 
                      className="bg-slate-900/50 border-slate-700 hover:border-slate-600 cursor-pointer transition-all"
                      onClick={() => {
                        setSelectedSection(code);
                        setActiveTab('standards');
                      }}
                      data-testid={`section-card-${code}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="p-2 rounded-lg bg-slate-800">
                            <Icon className="w-6 h-6 text-slate-300" />
                          </div>
                          <Badge variant="outline" className="text-xs">{config.items} ISARPs</Badge>
                        </div>
                        <h3 className="text-lg font-semibold text-white mt-3">{code}</h3>
                        <p className="text-sm text-slate-400">{config.name}</p>
                        {findingsCount > 0 && (
                          <Badge className="mt-2 bg-red-500/20 text-red-400">{findingsCount} findings</Badge>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Recent Audits */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white">Recent IOSA Audits</CardTitle>
                  <CardDescription>Latest audit activities</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => setActiveTab('audits')}>
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {(dashboard?.recent_audits?.length > 0 || audits.length > 0) ? (
                <div className="space-y-3">
                  {(dashboard?.recent_audits || audits.slice(0, 5)).map((audit) => (
                    <div 
                      key={audit.id} 
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-900/50 cursor-pointer hover:bg-slate-900/70"
                      onClick={() => {
                        fetchAuditDetail(audit.id);
                        setShowAuditDetailDialog(true);
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${audit.status === 'completed' ? 'bg-green-500/20' : audit.status === 'in_progress' ? 'bg-amber-500/20' : 'bg-blue-500/20'}`}>
                          <ClipboardCheck className={`w-5 h-5 ${audit.status === 'completed' ? 'text-green-400' : audit.status === 'in_progress' ? 'text-amber-400' : 'text-blue-400'}`} />
                        </div>
                        <div>
                          <p className="font-medium text-white">{audit.audit_number}</p>
                          <p className="text-sm text-slate-400">{audit.organization_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={getStatusColor(audit.status)}>
                          {audit.status?.replace(/_/g, ' ')}
                        </Badge>
                        <span className="text-xs text-slate-500">{audit.methodology?.toUpperCase()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ClipboardCheck className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No audits yet</p>
                  <Button className="mt-3" onClick={() => setShowNewAuditDialog(true)}>
                    Create First IOSA Audit
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Stats Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Recent Findings */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    Recent Findings
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('findings')}>
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {findings?.findings?.length > 0 ? (
                  <div className="space-y-2">
                    {findings.findings.slice(0, 4).map((finding) => (
                      <div key={finding.id} className="flex items-center justify-between p-2 rounded bg-slate-900/50">
                        <div className="flex items-center gap-2">
                          <Badge className={finding.finding_type === 'finding' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'} variant="outline">
                            {finding.section}
                          </Badge>
                          <span className="text-sm text-white truncate max-w-[200px]">{finding.title}</span>
                        </div>
                        <Badge className={finding.status === 'closed' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                          {finding.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm text-center py-4">No findings recorded</p>
                )}
              </CardContent>
            </Card>

            {/* Key IOSA Info */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Award className="w-5 h-5 text-amber-400" />
                  {t('iosa.quickReference.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="p-3 rounded bg-slate-900/50">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-400">{t('iosa.quickReference.currentEdition')}</span>
                      <Badge className="bg-purple-500/20 text-purple-400">ISM Ed. 17 (2025)</Badge>
                    </div>
                  </div>
                  <div className="p-3 rounded bg-slate-900/50">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-400">{t('iosa.quickReference.methodology')}</span>
                      <Badge className="bg-indigo-500/20 text-indigo-400">{t('iosa.methodology.rbi')}</Badge>
                    </div>
                  </div>
                  <div className="p-3 rounded bg-slate-900/50">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-400">{t('iosa.quickReference.registrationValidity')}</span>
                      <span className="text-sm text-white">{t('iosa.quickReference.twoYears')}</span>
                    </div>
                  </div>
                  <div className="p-3 rounded bg-slate-900/50">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-400">{t('iosa.quickReference.renewalTimeline')}</span>
                      <span className="text-sm text-white">{t('iosa.quickReference.sixToNineMonths')}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* AUDITS TAB */}
        <TabsContent value="audits" className="space-y-6 mt-6">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white">{t('iosa.audits')}</CardTitle>
                  <CardDescription>Manage initial, renewal, and special audits</CardDescription>
                </div>
                <Button onClick={() => setShowNewAuditDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Audit
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {audits.length > 0 ? (
                <div className="space-y-3">
                  {audits.map((audit) => (
                    <div 
                      key={audit.id} 
                      className="p-4 rounded-lg bg-slate-900/50 border border-slate-700 cursor-pointer hover:border-slate-600"
                      onClick={() => {
                        fetchAuditDetail(audit.id);
                        setShowAuditDetailDialog(true);
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-3">
                            <h3 className="text-lg font-semibold text-white">{audit.audit_number}</h3>
                            <Badge variant="outline">{audit.audit_type}</Badge>
                            <Badge className={audit.methodology === 'rbi' ? 'bg-purple-500/20 text-purple-400' : 'bg-slate-500/20 text-slate-400'}>
                              {audit.methodology?.toUpperCase()}
                            </Badge>
                          </div>
                          <p className="text-slate-400 mt-1">{audit.organization_name}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                            <span className="flex items-center gap-1"><User className="w-3 h-3" /> {audit.lead_auditor}</span>
                            <span className="flex items-center gap-1"><Target className="w-3 h-3" /> {audit.sections_in_scope?.length || 0}/8 sections</span>
                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {audit.planned_start_date} → {audit.planned_end_date}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={getStatusColor(audit.status)}>
                            {audit.status?.replace(/_/g, ' ')}
                          </Badge>
                          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); fetchAuditDetail(audit.id); setShowAuditDetailDialog(true); }}>
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className="mt-4">
                        <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                          <span>Progress</span>
                          <span>{audit.total_isarps_assessed || 0} / {audit.sections_in_scope?.reduce((sum, s) => sum + (IOSA_SECTIONS[s]?.items || 0), 0) || 0} ISARPs</span>
                        </div>
                        <Progress 
                          value={audit.sections_in_scope?.length > 0 
                            ? ((audit.total_isarps_assessed || 0) / audit.sections_in_scope.reduce((sum, s) => sum + (IOSA_SECTIONS[s]?.items || 0), 0)) * 100 
                            : 0
                          } 
                          className="h-2" 
                        />
                      </div>
                      {/* Stats */}
                      <div className="flex items-center gap-4 mt-3">
                        <span className="text-xs text-green-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {audit.conforming_isarps || 0} Conforming</span>
                        <span className="text-xs text-red-400 flex items-center gap-1"><XCircle className="w-3 h-3" /> {audit.non_conforming_isarps || 0} Non-conforming</span>
                        <span className="text-xs text-slate-400">{audit.not_applicable_isarps || 0} N/A</span>
                        <span className="text-xs text-orange-400 flex items-center gap-1"><FileWarning className="w-3 h-3" /> {audit.total_findings || 0} Findings</span>
                        <span className="text-xs text-amber-400">{audit.total_observations || 0} Observations</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <ClipboardCheck className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">No IOSA Audits</h3>
                  <p className="text-slate-400 mb-4">Create your first IOSA audit to get started</p>
                  <Button onClick={() => setShowNewAuditDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create IOSA Audit
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* STANDARDS TAB */}
        <TabsContent value="standards" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Section Selector */}
            <Card className="bg-slate-800/50 border-slate-700 lg:col-span-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg">Sections</CardTitle>
              </CardHeader>
              <CardContent className="p-2">
                <ScrollArea className="h-[500px]">
                  {Object.entries(IOSA_SECTIONS).map(([code, config]) => {
                    const Icon = config.icon;
                    return (
                      <div
                        key={code}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${selectedSection === code ? 'bg-purple-500/20 border border-purple-500/50' : 'hover:bg-slate-700/50'}`}
                        onClick={() => setSelectedSection(code)}
                      >
                        <Icon className="w-5 h-5 text-slate-400" />
                        <div className="flex-1">
                          <p className="font-medium text-white">{code}</p>
                          <p className="text-xs text-slate-400">{config.name}</p>
                        </div>
                        <Badge variant="outline" className="text-xs">{config.items}</Badge>
                      </div>
                    );
                  })}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Section Detail */}
            <Card className="bg-slate-800/50 border-slate-700 lg:col-span-3">
              <CardHeader>
                {selectedSection ? (
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white flex items-center gap-2">
                        {(() => { const Icon = IOSA_SECTIONS[selectedSection]?.icon || Building2; return <Icon className="w-5 h-5 text-purple-400" />; })()}
                        {selectedSection} - {IOSA_SECTIONS[selectedSection]?.name}
                      </CardTitle>
                      <CardDescription>{sectionDetail?.description}</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm">
                        <Download className="w-4 h-4 mr-1" />
                        Export Checklist
                      </Button>
                    </div>
                  </div>
                ) : (
                  <CardTitle className="text-white">Select a Section</CardTitle>
                )}
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
                  </div>
                ) : selectedSection && sectionDetail ? (
                  <div className="space-y-4">
                    {/* Subsections */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                      {sectionDetail.subsections?.map((sub) => (
                        <div key={sub.code} className="p-3 rounded-lg bg-slate-900/50">
                          <p className="font-medium text-white">{sub.code}</p>
                          <p className="text-xs text-slate-400">{sub.name}</p>
                          <Badge variant="outline" className="mt-1 text-xs">{sub.items} items</Badge>
                        </div>
                      ))}
                    </div>

                    {/* ISARPs List */}
                    <Accordion type="single" collapsible className="space-y-2">
                      {sectionDetail.isarps?.map((isarp) => (
                        <AccordionItem key={isarp.id || isarp.code} value={isarp.code} className="border border-slate-700 rounded-lg bg-slate-900/30">
                          <AccordionTrigger className="px-4 py-3 hover:no-underline">
                            <div className="flex items-center gap-3 text-left">
                              <Badge variant={isarp.type === 'standard' ? 'default' : 'secondary'} className="text-xs">
                                {isarp.type === 'standard' ? 'STD' : 'RP'}
                              </Badge>
                              <span className="font-mono text-sm text-slate-300">{isarp.code}</span>
                              <span className="text-white">{isarp.title}</span>
                              {isarp.mandatory_observation && (
                                <Badge className="bg-amber-500/20 text-amber-400 text-xs">RBI</Badge>
                              )}
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4">
                            <p className="text-slate-400 mb-3">{isarp.description}</p>
                            {isarp.guidance && (
                              <div className="p-3 rounded bg-slate-800/50 mb-3">
                                <p className="text-xs text-slate-500 uppercase mb-1">Guidance</p>
                                <p className="text-sm text-slate-300">{isarp.guidance}</p>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className={`text-xs ${isarp.risk_level === 'high' ? 'border-red-500 text-red-400' : isarp.risk_level === 'medium' ? 'border-amber-500 text-amber-400' : 'border-green-500 text-green-400'}`}>
                                {isarp.risk_level || 'medium'} risk
                              </Badge>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">Select a section to view ISARPs and checklists</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* FINDINGS TAB */}
        <TabsContent value="findings" className="space-y-6 mt-6">
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <p className="text-xs text-slate-400 uppercase">Total Findings</p>
                <p className="text-2xl font-bold text-white">{findings?.stats?.total || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <p className="text-xs text-slate-400 uppercase">Open</p>
                <p className="text-2xl font-bold text-red-400">{findings?.stats?.open || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <p className="text-xs text-slate-400 uppercase">Level 1 (Minor)</p>
                <p className="text-2xl font-bold text-yellow-400">{findings?.stats?.level_1 || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <p className="text-xs text-slate-400 uppercase">Level 2 (Significant)</p>
                <p className="text-2xl font-bold text-orange-400">{findings?.stats?.level_2 || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <p className="text-xs text-slate-400 uppercase">Level 3 (Major)</p>
                <p className="text-2xl font-bold text-red-400">{findings?.stats?.level_3 || 0}</p>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white">Findings & Observations</CardTitle>
                  <CardDescription>Track non-conformities and corrective actions</CardDescription>
                </div>
                <Button onClick={() => { if (audits.length > 0) { setSelectedAudit(audits[0]); setShowFindingDialog(true); } else { toast.error('Create an audit first'); } }}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Finding
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {findings?.findings?.length > 0 ? (
                <div className="space-y-3">
                  {findings.findings.map((finding) => (
                    <div key={finding.id} className="p-4 rounded-lg bg-slate-900/50 border border-slate-700">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={finding.finding_type === 'finding' ? 'bg-red-500/20 text-red-400' : finding.finding_type === 'observation' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'}>
                              {finding.finding_type}
                            </Badge>
                            {finding.severity && (
                              <Badge className={getSeverityColor(finding.severity)}>
                                {finding.severity?.replace('_', ' ')}
                              </Badge>
                            )}
                            <span className="text-xs text-slate-500 font-mono">{finding.finding_number}</span>
                            <span className="text-xs text-slate-500">{finding.isarp_code}</span>
                          </div>
                          <h4 className="text-white font-medium">{finding.title}</h4>
                          <p className="text-sm text-slate-400 mt-1 line-clamp-2">{finding.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span>Section: {finding.section}</span>
                            {finding.due_date && <span>Due: {finding.due_date}</span>}
                            <span>By: {finding.created_by}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={finding.status === 'closed' ? 'bg-green-500/20 text-green-400' : finding.status === 'in_progress' ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'}>
                            {finding.status}
                          </Badge>
                          <Button variant="outline" size="sm" onClick={() => openEvidenceDialog(finding)} data-testid={`evidence-btn-${finding.id}`}>
                            <Paperclip className="w-4 h-4 mr-1" />
                            Evidence {finding.evidence_count > 0 && `(${finding.evidence_count})`}
                          </Button>
                          {finding.status !== 'closed' && (
                            <Button variant="outline" size="sm" onClick={() => updateFindingStatus(finding.id, 'closed')}>
                              Close
                            </Button>
                          )}
                        </div>
                      </div>
                      {finding.corrective_action_required && (
                        <div className="mt-3 p-3 rounded bg-slate-800/50">
                          <p className="text-xs text-slate-500 uppercase mb-1">Corrective Action Required</p>
                          <p className="text-sm text-slate-300">{finding.corrective_action_required}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <AlertTriangle className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No findings recorded yet</p>
                  <p className="text-sm text-slate-500 mt-1">Findings will appear here as audits are conducted</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SMS MATURITY TAB */}
        <TabsContent value="sms-maturity" className="space-y-6 mt-6">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-purple-400" />
                    SMS Maturity Assessment
                  </CardTitle>
                  <CardDescription>Risk-Based IOSA (RBI) 2025 methodology - Assess SMS implementation maturity</CardDescription>
                </div>
                {audits.length > 0 && (
                  <Button onClick={() => { setSelectedAudit(audits[0]); setShowSMSMaturityDialog(true); }}>
                    <Plus className="w-4 h-4 mr-2" />
                    New Assessment
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {[
                  { name: 'Safety Policy & Objectives', level: smsMaturity?.policy_objectives || 3, max: 5, icon: Target },
                  { name: 'Safety Risk Management', level: smsMaturity?.risk_management || 3, max: 5, icon: AlertTriangle },
                  { name: 'Safety Assurance', level: smsMaturity?.safety_assurance || 3, max: 5, icon: Shield },
                  { name: 'Safety Promotion', level: smsMaturity?.safety_promotion || 3, max: 5, icon: Users }
                ].map((pillar, idx) => {
                  const Icon = pillar.icon;
                  return (
                    <Card key={idx} className="bg-slate-900/50 border-slate-700">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className="w-4 h-4 text-purple-400" />
                          <p className="text-sm text-slate-400">{pillar.name}</p>
                        </div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-3xl font-bold text-white">{pillar.level}</span>
                          <span className="text-sm text-slate-500">/ {pillar.max}</span>
                        </div>
                        <Progress value={(pillar.level / pillar.max) * 100} className="h-2" />
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Maturity Level Description */}
              <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
                <h3 className="text-lg font-semibold text-white mb-3">SMS Maturity Levels</h3>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                  {[
                    { level: 1, name: 'Initial', desc: 'Ad hoc processes' },
                    { level: 2, name: 'Defined', desc: 'Documented processes' },
                    { level: 3, name: 'Implemented', desc: 'Processes in use' },
                    { level: 4, name: 'Managed', desc: 'Measured & controlled' },
                    { level: 5, name: 'Optimized', desc: 'Continuous improvement' }
                  ].map((lvl) => (
                    <div key={lvl.level} className={`p-3 rounded-lg ${lvl.level === (smsMaturity?.overall_level || 3) ? 'bg-purple-500/30 border border-purple-500' : 'bg-slate-800/50'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center text-sm font-bold text-purple-400">{lvl.level}</span>
                        <span className="font-medium text-white">{lvl.name}</span>
                      </div>
                      <p className="text-xs text-slate-400">{lvl.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Strengths & Improvements */}
              {smsMaturity?.exists && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                  <Card className="bg-green-500/10 border-green-500/30">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-green-400 text-sm">Strengths</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-1">
                        {smsMaturity.strengths?.map((s, i) => (
                          <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                            {s}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-500/10 border-amber-500/30">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-amber-400 text-sm">Improvement Areas</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-1">
                        {smsMaturity.improvement_areas?.map((s, i) => (
                          <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                            {s}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-500/10 border-blue-500/30">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-blue-400 text-sm">Recommendations</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-1">
                        {smsMaturity.recommendations?.map((s, i) => (
                          <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                            <TrendingUp className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                            {s}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* INTEGRATIONS TAB - IATA UPDATES & IDX */}
        <TabsContent value="integrations" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* IATA Updates Section */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-blue-400" />
                  {t('iosa.updates.title')}
                </CardTitle>
                <CardDescription>{t('iosa.subtitle')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Current Version */}
                <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-700">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-slate-400">{t('iosa.updates.currentVersion')}</span>
                    <Badge className="bg-green-500/20 text-green-400">
                      {iataUpdateStatus?.current_version?.edition || 'ISM Ed. 17'}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Version</p>
                      <p className="text-white font-medium">{iataUpdateStatus?.current_version?.version || '17.0'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">{t('iosa.totalIsarps')}</p>
                      <p className="text-white font-medium">{iataUpdateStatus?.current_version?.isarps_count || 925}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">{t('common.date')}</p>
                      <p className="text-white font-medium">{iataUpdateStatus?.current_version?.release_date || '2025-01-01'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">{t('iosa.idx.lastSync')}</p>
                      <p className="text-white font-medium">{iataUpdateStatus?.current_version?.last_checked ? new Date(iataUpdateStatus.current_version.last_checked).toLocaleDateString() : 'Never'}</p>
                    </div>
                  </div>
                </div>

                {/* Update Available */}
                {iataUpdateStatus?.update_available && (
                  <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <div className="flex items-center gap-2 mb-3">
                      <Bell className="w-5 h-5 text-amber-400" />
                      <span className="text-amber-400 font-medium">{t('iosa.updates.updateAvailable')}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                      <div>
                        <p className="text-slate-500">{t('iosa.updates.latestVersion')}</p>
                        <p className="text-white font-medium">{iataUpdateStatus?.latest_version?.version}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">{t('iosa.totalIsarps')}</p>
                        <p className="text-white font-medium">{iataUpdateStatus?.latest_version?.isarps_count}</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-400 mb-3">{iataUpdateStatus?.latest_version?.release_notes}</p>
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                      <span className="text-green-400">+{iataUpdateStatus?.latest_version?.changes?.[0]?.count} added</span>
                      <span className="text-blue-400">{iataUpdateStatus?.latest_version?.changes?.[1]?.count} modified</span>
                      <span className="text-red-400">-{iataUpdateStatus?.latest_version?.changes?.[2]?.count} removed</span>
                    </div>
                    <Button onClick={applyIataUpdate} disabled={loading} className="w-full">
                      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                      {t('iosa.updates.applyUpdate')}
                    </Button>
                  </div>
                )}

                {!iataUpdateStatus?.update_available && (
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30 text-center">
                    <CheckCircle2 className="w-8 h-8 text-green-400 mx-auto mb-2" />
                    <p className="text-green-400 font-medium">{t('iosa.updates.upToDate')}</p>
                    <p className="text-sm text-slate-400">{t('iosa.updates.latestVersion')}</p>
                  </div>
                )}

                <Button variant="outline" onClick={checkForUpdates} disabled={checkingUpdates} className="w-full">
                  {checkingUpdates ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  {t('iosa.updates.checkUpdates')}
                </Button>
              </CardContent>
            </Card>

            {/* IDX Integration Section */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Globe className="w-5 h-5 text-purple-400" />
                  {t('iosa.idx.title')}
                </CardTitle>
                <CardDescription>{t('iosa.idx.subtitle')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* IDX Status */}
                <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-700">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-slate-400">{t('common.status')}</span>
                    <Badge className={idxStatus?.status === 'connected' ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'}>
                      {idxStatus?.status === 'connected' ? t('common.active') : t('common.inactive')}
                    </Badge>
                  </div>
                  {idxStatus?.config?.last_sync && (
                    <div className="text-sm">
                      <p className="text-slate-500">Last Sync</p>
                      <p className="text-white">{new Date(idxStatus.config.last_sync).toLocaleString()}</p>
                    </div>
                  )}
                </div>

                {/* Sync Actions */}
                <div className="grid grid-cols-2 gap-3">
                  <Button variant="outline" onClick={() => syncIdxData('export')} disabled={syncingIdx}>
                    <Share2 className="w-4 h-4 mr-2" />
                    Export to IDX
                  </Button>
                  <Button variant="outline" onClick={() => syncIdxData('import')} disabled={syncingIdx}>
                    <Download className="w-4 h-4 mr-2" />
                    Import from IDX
                  </Button>
                </div>

                <Button onClick={() => syncIdxData('both')} disabled={syncingIdx} className="w-full">
                  {syncingIdx ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowUpDown className="w-4 h-4 mr-2" />}
                  Full Sync (Both Ways)
                </Button>

                <Button variant="outline" onClick={importIdxAlerts} disabled={loading} className="w-full">
                  <Bell className="w-4 h-4 mr-2" />
                  Import Industry Alerts
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* IDX Safety Alerts */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Bell className="w-5 h-5 text-amber-400" />
                    IDX Industry Safety Alerts
                  </CardTitle>
                  <CardDescription>De-identified safety alerts from the global aviation community</CardDescription>
                </div>
                <Badge variant="outline">{idxAlerts.length} alerts</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {idxAlerts.length > 0 ? (
                <div className="space-y-3">
                  {idxAlerts.map((alert) => (
                    <div key={alert.id} className="p-4 rounded-lg bg-slate-900/50 border border-slate-700">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge className={alert.severity === 'high' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}>
                            {alert.severity}
                          </Badge>
                          <span className="text-xs text-slate-500 font-mono">{alert.idx_alert_id}</span>
                        </div>
                        <span className="text-xs text-slate-500">{alert.effective_date}</span>
                      </div>
                      <h4 className="text-white font-medium mb-1">{alert.title}</h4>
                      <p className="text-sm text-slate-400 mb-2">{alert.description}</p>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-slate-500">Affected:</span>
                        {alert.affected_operations?.map((op) => (
                          <Badge key={op} variant="outline" className="text-xs">{op}</Badge>
                        ))}
                      </div>
                      {alert.recommended_actions?.length > 0 && (
                        <div className="mt-2 p-2 rounded bg-slate-800/50">
                          <p className="text-xs text-slate-500 uppercase mb-1">Recommended Actions</p>
                          <ul className="text-sm text-slate-300 list-disc list-inside">
                            {alert.recommended_actions.map((action, idx) => (
                              <li key={idx}>{action}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Globe className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No IDX alerts imported yet</p>
                  <p className="text-sm text-slate-500 mt-1">Click "Import Industry Alerts" to fetch latest safety bulletins</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* NEW AUDIT DIALOG */}
      <Dialog open={showNewAuditDialog} onOpenChange={setShowNewAuditDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-amber-400" />
              Create New IOSA Audit
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Audit Type</Label>
                <Select value={auditForm.audit_type} onValueChange={(v) => setAuditForm({...auditForm, audit_type: v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="initial">Initial Audit</SelectItem>
                    <SelectItem value="renewal">Renewal Audit</SelectItem>
                    <SelectItem value="special">Special Audit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Methodology</Label>
                <Select value={auditForm.methodology} onValueChange={(v) => setAuditForm({...auditForm, methodology: v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rbi">Risk-Based IOSA (RBI)</SelectItem>
                    <SelectItem value="traditional">Traditional</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Organization Name *</Label>
              <Input
                value={auditForm.organization_name}
                onChange={(e) => setAuditForm({...auditForm, organization_name: e.target.value})}
                className="bg-slate-800 border-slate-700"
                placeholder="e.g., Saudi Arabian Airlines"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>AOC Number</Label>
                <Input
                  value={auditForm.aoc_number}
                  onChange={(e) => setAuditForm({...auditForm, aoc_number: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>IATA Code</Label>
                <Input
                  value={auditForm.iata_code}
                  onChange={(e) => setAuditForm({...auditForm, iata_code: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                  placeholder="e.g., SV"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Planned Start Date *</Label>
                <Input
                  type="date"
                  value={auditForm.planned_start_date}
                  onChange={(e) => setAuditForm({...auditForm, planned_start_date: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Planned End Date *</Label>
                <Input
                  type="date"
                  value={auditForm.planned_end_date}
                  onChange={(e) => setAuditForm({...auditForm, planned_end_date: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Lead Auditor *</Label>
              <Input
                value={auditForm.lead_auditor}
                onChange={(e) => setAuditForm({...auditForm, lead_auditor: e.target.value})}
                className="bg-slate-800 border-slate-700"
              />
            </div>

            <div className="space-y-2">
              <Label>Sections in Scope</Label>
              <div className="grid grid-cols-4 gap-2">
                {Object.entries(IOSA_SECTIONS).map(([code, config]) => {
                  const Icon = config.icon;
                  const isSelected = auditForm.sections_in_scope.includes(code);
                  return (
                    <div
                      key={code}
                      className={`p-2 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-purple-500/30 border border-purple-500' : 'bg-slate-800 border border-slate-700 hover:border-slate-600'}`}
                      onClick={() => {
                        if (isSelected) {
                          setAuditForm({...auditForm, sections_in_scope: auditForm.sections_in_scope.filter(s => s !== code)});
                        } else {
                          setAuditForm({...auditForm, sections_in_scope: [...auditForm.sections_in_scope, code]});
                        }
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-white">{code}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewAuditDialog(false)}>Cancel</Button>
            <Button onClick={createAudit} disabled={loading || !auditForm.organization_name || !auditForm.planned_start_date || !auditForm.lead_auditor}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Audit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AUDIT DETAIL DIALOG */}
      <Dialog open={showAuditDetailDialog} onOpenChange={setShowAuditDetailDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-400" />
              {auditDetail?.audit?.audit_number} - {auditDetail?.audit?.organization_name}
            </DialogTitle>
          </DialogHeader>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
            </div>
          ) : auditDetail ? (
            <div className="space-y-6 py-4">
              {/* Audit Overview Cards */}
              <div className="grid grid-cols-4 gap-4">
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-xs text-slate-400">Status</p>
                  <Badge className={getStatusColor(auditDetail.audit?.status)}>{auditDetail.audit?.status?.replace(/_/g, ' ')}</Badge>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-xs text-slate-400">Methodology</p>
                  <p className="text-white font-medium">{auditDetail.audit?.methodology?.toUpperCase()}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-xs text-slate-400">Lead Auditor</p>
                  <p className="text-white font-medium">{auditDetail.audit?.lead_auditor}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-xs text-slate-400">Audit Type</p>
                  <p className="text-white font-medium capitalize">{auditDetail.audit?.audit_type}</p>
                </div>
              </div>

              {/* Timeline / Schedule */}
              <div className="p-4 rounded-lg bg-slate-800/30 border border-slate-700">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-400" />
                  Audit Schedule
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Documentation Review</p>
                    <p className="text-sm text-white">
                      {auditDetail.audit?.documentation_review_start || auditDetail.audit?.planned_start_date || 'TBD'} 
                      {' → '}
                      {auditDetail.audit?.documentation_review_end || 'TBD'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Onsite Audit</p>
                    <p className="text-sm text-white">
                      {auditDetail.audit?.onsite_start || auditDetail.audit?.planned_start_date || 'TBD'}
                      {' → '}
                      {auditDetail.audit?.onsite_end || auditDetail.audit?.planned_end_date || 'TBD'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Planned Dates</p>
                    <p className="text-sm text-white">
                      {auditDetail.audit?.planned_start_date} → {auditDetail.audit?.planned_end_date}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Actual End</p>
                    <p className="text-sm text-white">{auditDetail.audit?.actual_end_date || 'In Progress'}</p>
                  </div>
                </div>
              </div>

              {/* Audit Team */}
              {auditDetail.audit?.audit_team?.length > 0 && (
                <div>
                  <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4 text-purple-400" />
                    Audit Team ({auditDetail.audit.audit_team.length} members)
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {auditDetail.audit.audit_team.map((member, idx) => (
                      <div key={member.id || idx} className="p-3 rounded-lg bg-slate-800/50 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                          <User className="w-4 h-4 text-purple-400" />
                        </div>
                        <div>
                          <p className="text-sm text-white">{member.name}</p>
                          <p className="text-xs text-slate-400">{member.role || 'Auditor'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Overall Progress Summary */}
              <div className="p-4 rounded-lg bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/30">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-medium">Overall Audit Progress</h4>
                  <span className="text-2xl font-bold text-white">
                    {auditDetail.audit?.total_isarps_assessed || 0} / {auditDetail.audit?.sections_in_scope?.reduce((sum, s) => sum + (IOSA_SECTIONS[s]?.items || 0), 0) || 925}
                  </span>
                </div>
                <Progress 
                  value={((auditDetail.audit?.total_isarps_assessed || 0) / (auditDetail.audit?.sections_in_scope?.reduce((sum, s) => sum + (IOSA_SECTIONS[s]?.items || 0), 0) || 925)) * 100} 
                  className="h-3 mb-3" 
                />
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-xl font-bold text-green-400">{auditDetail.audit?.conforming_isarps || 0}</p>
                    <p className="text-xs text-slate-400">Conforming</p>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-red-400">{auditDetail.audit?.non_conforming_isarps || 0}</p>
                    <p className="text-xs text-slate-400">Non-Conforming</p>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-slate-400">{auditDetail.audit?.not_applicable_isarps || 0}</p>
                    <p className="text-xs text-slate-400">Not Applicable</p>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-amber-400">{auditDetail.audit?.total_findings || 0}</p>
                    <p className="text-xs text-slate-400">Findings</p>
                  </div>
                </div>
              </div>

              {/* Section Progress */}
              <div>
                <h4 className="text-white font-medium mb-3">Section Progress</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(auditDetail.section_progress || {}).map(([section, progress]) => {
                    const config = IOSA_SECTIONS[section];
                    const Icon = config?.icon || Building2;
                    return (
                      <div key={section} className="p-3 rounded-lg bg-slate-800/50">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className="w-4 h-4 text-slate-400" />
                          <span className="text-white font-medium">{section}</span>
                        </div>
                        <Progress value={progress.progress_percent} className="h-2 mb-1" />
                        <p className="text-xs text-slate-400">{progress.assessed}/{progress.total_isarps} assessed</p>
                        <div className="flex gap-2 mt-1">
                          <span className="text-xs text-green-400">{progress.conforming} ✓</span>
                          <span className="text-xs text-red-400">{progress.non_conforming} ✗</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Findings Summary */}
              {auditDetail.findings?.length > 0 && (
                <div>
                  <h4 className="text-white font-medium mb-3">Findings ({auditDetail.findings.length})</h4>
                  <div className="space-y-2">
                    {auditDetail.findings.slice(0, 5).map((finding) => (
                      <div key={finding.id} className="p-3 rounded-lg bg-slate-800/50 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Badge className={finding.finding_type === 'finding' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}>
                            {finding.finding_type}
                          </Badge>
                          <span className="text-white">{finding.title}</span>
                        </div>
                        <Badge className={finding.status === 'closed' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                          {finding.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <Button onClick={() => { setShowAuditDetailDialog(false); setShowFindingDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Finding
                </Button>
                <Button variant="outline" onClick={() => { setShowAuditDetailDialog(false); setShowSMSMaturityDialog(true); }}>
                  <TrendingUp className="w-4 h-4 mr-2" />
                  SMS Maturity
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* FINDING DIALOG */}
      <Dialog open={showFindingDialog} onOpenChange={setShowFindingDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Create Finding
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Finding Type</Label>
                <Select value={findingForm.finding_type} onValueChange={(v) => setFindingForm({...findingForm, finding_type: v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FINDING_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Severity</Label>
                <Select value={findingForm.severity} onValueChange={(v) => setFindingForm({...findingForm, severity: v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEVERITY_LEVELS.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>ISARP Code</Label>
              <Input
                value={findingForm.isarp_code || selectedISARP?.code || ''}
                onChange={(e) => setFindingForm({...findingForm, isarp_code: e.target.value})}
                className="bg-slate-800 border-slate-700"
                placeholder="e.g., ORG 2.1.1"
              />
            </div>

            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={findingForm.title}
                onChange={(e) => setFindingForm({...findingForm, title: e.target.value})}
                className="bg-slate-800 border-slate-700"
              />
            </div>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={findingForm.description}
                onChange={(e) => setFindingForm({...findingForm, description: e.target.value})}
                className="bg-slate-800 border-slate-700 min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label>Root Cause</Label>
              <Textarea
                value={findingForm.root_cause}
                onChange={(e) => setFindingForm({...findingForm, root_cause: e.target.value})}
                className="bg-slate-800 border-slate-700"
              />
            </div>

            <div className="space-y-2">
              <Label>Corrective Action Required *</Label>
              <Textarea
                value={findingForm.corrective_action_required}
                onChange={(e) => setFindingForm({...findingForm, corrective_action_required: e.target.value})}
                className="bg-slate-800 border-slate-700"
              />
            </div>

            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input
                type="date"
                value={findingForm.due_date}
                onChange={(e) => setFindingForm({...findingForm, due_date: e.target.value})}
                className="bg-slate-800 border-slate-700"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFindingDialog(false)}>Cancel</Button>
            <Button onClick={createFinding} disabled={loading || !findingForm.title || !findingForm.description || !findingForm.corrective_action_required}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Finding
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SMS MATURITY DIALOG */}
      <Dialog open={showSMSMaturityDialog} onOpenChange={setShowSMSMaturityDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-400" />
              SMS Maturity Assessment
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Maturity Sliders */}
            {[
              { key: 'policy_objectives', label: 'Safety Policy & Objectives' },
              { key: 'risk_management', label: 'Safety Risk Management' },
              { key: 'safety_assurance', label: 'Safety Assurance' },
              { key: 'safety_promotion', label: 'Safety Promotion' }
            ].map((pillar) => (
              <div key={pillar.key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>{pillar.label}</Label>
                  <span className="text-lg font-bold text-purple-400">{smsMaturityForm[pillar.key]}</span>
                </div>
                <Slider
                  value={[smsMaturityForm[pillar.key]]}
                  onValueChange={(v) => setSmsMaturityForm({...smsMaturityForm, [pillar.key]: v[0]})}
                  min={1}
                  max={5}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-slate-500">
                  <span>1 - Initial</span>
                  <span>2 - Defined</span>
                  <span>3 - Implemented</span>
                  <span>4 - Managed</span>
                  <span>5 - Optimized</span>
                </div>
              </div>
            ))}

            <div className="space-y-2">
              <Label>Strengths (one per line)</Label>
              <Textarea
                value={smsMaturityForm.strengths}
                onChange={(e) => setSmsMaturityForm({...smsMaturityForm, strengths: e.target.value})}
                className="bg-slate-800 border-slate-700"
                placeholder="Strong safety policy with executive commitment&#10;Active safety reporting culture"
              />
            </div>

            <div className="space-y-2">
              <Label>Improvement Areas (one per line)</Label>
              <Textarea
                value={smsMaturityForm.improvement_areas}
                onChange={(e) => setSmsMaturityForm({...smsMaturityForm, improvement_areas: e.target.value})}
                className="bg-slate-800 border-slate-700"
                placeholder="Risk assessment needs more systematic approach&#10;Safety promotion tracking"
              />
            </div>

            <div className="space-y-2">
              <Label>Recommendations (one per line)</Label>
              <Textarea
                value={smsMaturityForm.recommendations}
                onChange={(e) => setSmsMaturityForm({...smsMaturityForm, recommendations: e.target.value})}
                className="bg-slate-800 border-slate-700"
                placeholder="Implement predictive safety analysis&#10;Enhance SPI tracking"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSMSMaturityDialog(false)}>Cancel</Button>
            <Button onClick={saveSMSMaturity} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Save Assessment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EVIDENCE MANAGEMENT DIALOG */}
      <Dialog open={showEvidenceDialog} onOpenChange={setShowEvidenceDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="w-5 h-5 text-blue-400" />
              {t('iosa.evidence.title')}
            </DialogTitle>
            {selectedFindingForEvidence && (
              <p className="text-sm text-slate-400">
                {selectedFindingForEvidence.finding_number} - {selectedFindingForEvidence.title}
              </p>
            )}
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Upload Section */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-white flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  {t('iosa.evidence.upload')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label>{t('common.status')}</Label>
                    <Select 
                      value={evidenceForm.evidence_type} 
                      onValueChange={(v) => setEvidenceForm({...evidenceForm, evidence_type: v})}
                    >
                      <SelectTrigger className="bg-slate-700 border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="document">
                          <div className="flex items-center gap-2">
                            <File className="w-4 h-4" />
                            {t('iosa.evidence.document')}
                          </div>
                        </SelectItem>
                        <SelectItem value="photo">
                          <div className="flex items-center gap-2">
                            <Image className="w-4 h-4" />
                            {t('iosa.evidence.photo')}
                          </div>
                        </SelectItem>
                        <SelectItem value="recording">
                          <div className="flex items-center gap-2">
                            <Mic className="w-4 h-4" />
                            {t('iosa.evidence.recording')}
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>{t('iosa.evidence.description')}</Label>
                    <Input
                      value={evidenceForm.description}
                      onChange={(e) => setEvidenceForm({...evidenceForm, description: e.target.value})}
                      className="bg-slate-700 border-slate-600"
                      placeholder={t('iosa.evidence.description') + '...'}
                    />
                  </div>
                </div>
                
                {evidenceForm.evidence_type === 'recording' && (
                  <div className="space-y-2">
                    <Label>Interview Subject</Label>
                    <Input
                      value={evidenceForm.interview_subject}
                      onChange={(e) => setEvidenceForm({...evidenceForm, interview_subject: e.target.value})}
                      className="bg-slate-700 border-slate-600"
                      placeholder="Name of interviewee..."
                    />
                  </div>
                )}
                
                <div className="border-2 border-dashed border-slate-600 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    id="evidence-upload"
                    className="hidden"
                    accept={evidenceForm.evidence_type === 'document' ? '.pdf,.doc,.docx' : evidenceForm.evidence_type === 'photo' ? '.jpg,.jpeg,.png,.webp' : '.mp3,.wav,.m4a'}
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        uploadEvidence(e.target.files[0]);
                      }
                    }}
                  />
                  <label htmlFor="evidence-upload" className="cursor-pointer">
                    {uploadingEvidence ? (
                      <Loader2 className="w-8 h-8 text-slate-400 mx-auto animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-slate-400">Click to upload or drag and drop</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {evidenceForm.evidence_type === 'document' ? 'PDF, DOC, DOCX' : 
                           evidenceForm.evidence_type === 'photo' ? 'JPG, PNG, WEBP' : 'MP3, WAV, M4A'}
                        </p>
                      </>
                    )}
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Evidence List */}
            <div className="space-y-4">
              {/* Documents */}
              {findingEvidence.by_type?.documents?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-white flex items-center gap-2 mb-2">
                    <File className="w-4 h-4 text-blue-400" />
                    Documents ({findingEvidence.by_type.documents.length})
                  </h4>
                  <div className="space-y-2">
                    {findingEvidence.by_type.documents.map((ev) => (
                      <div key={ev.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                        <div className="flex items-center gap-3">
                          <File className="w-5 h-5 text-blue-400" />
                          <div>
                            <p className="text-sm text-white">{ev.file_name}</p>
                            <p className="text-xs text-slate-500">{ev.description || 'No description'} • {(ev.file_size / 1024).toFixed(1)} KB</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">{ev.uploaded_by}</span>
                          <Button variant="ghost" size="sm" onClick={() => deleteEvidence(ev.id)}>
                            <X className="w-4 h-4 text-red-400" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Photos */}
              {findingEvidence.by_type?.photos?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-white flex items-center gap-2 mb-2">
                    <Image className="w-4 h-4 text-green-400" />
                    Photos ({findingEvidence.by_type.photos.length})
                  </h4>
                  <div className="grid grid-cols-3 gap-2">
                    {findingEvidence.by_type.photos.map((ev) => (
                      <div key={ev.id} className="relative group rounded-lg overflow-hidden bg-slate-800 border border-slate-700">
                        <div className="aspect-square flex items-center justify-center">
                          <Image className="w-12 h-12 text-slate-600" />
                        </div>
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Button variant="ghost" size="sm" onClick={() => deleteEvidence(ev.id)}>
                            <X className="w-5 h-5 text-red-400" />
                          </Button>
                        </div>
                        <p className="text-xs text-slate-400 p-2 truncate">{ev.file_name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recordings */}
              {findingEvidence.by_type?.recordings?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-white flex items-center gap-2 mb-2">
                    <Mic className="w-4 h-4 text-purple-400" />
                    Interview Recordings ({findingEvidence.by_type.recordings.length})
                  </h4>
                  <div className="space-y-2">
                    {findingEvidence.by_type.recordings.map((ev) => (
                      <div key={ev.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                        <div className="flex items-center gap-3">
                          <Mic className="w-5 h-5 text-purple-400" />
                          <div>
                            <p className="text-sm text-white">{ev.interview_subject || ev.file_name}</p>
                            <p className="text-xs text-slate-500">{ev.description || 'Interview recording'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">{ev.uploaded_by}</span>
                          <Button variant="ghost" size="sm" onClick={() => deleteEvidence(ev.id)}>
                            <X className="w-4 h-4 text-red-400" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {findingEvidence.total === 0 && (
                <div className="text-center py-8">
                  <Paperclip className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No evidence attached yet</p>
                  <p className="text-sm text-slate-500 mt-1">Upload documents, photos, or interview recordings</p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEvidenceDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
